import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create auth context
const AuthContext = createContext();

// API URL
const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token exists on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Get current user
  const getCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
      setError(null);
    } catch (error) {
      console.error('Error getting user:', error);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setError('Session expired. Please login again.');
    } finally {
      setLoading(false);
    }
  };

  // Login
  const login = async (username, password) => {
    setLoading(true);
    try {
      // Format data for token request
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(`${API_URL}/auth/token`, formData);
      const { access_token } = response.data;

      // Save token to local storage
      localStorage.setItem('token', access_token);
      
      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get user info
      await getCurrentUser();
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setUser(null);
      setIsAuthenticated(false);
      setError(error.response?.data?.detail || 'Login failed. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Register
  const register = async (username, email, password) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/register`, {
        username,
        email,
        password
      });
      
      // Login after registration
      return await login(username, password);
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail || 'Registration failed. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Auth context value
  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
